import React, { useState, useEffect } from 'react';
import logo from './img/ensight_logo.png';
import ReceiptAnalysisTab from './components/ReceiptAnalysisTab';
import ReviewTab from './components/ReviewTab';
import SearchTab from './components/SearchTab';
import LoginPage from './components/LoginPage';
import { useAuth } from './AuthContext';
import api from './api';
const SettingsModal = require('./components/SettingsModal').default as React.FC<any>;

interface ReceiptData {
  merchant_name: string | null;
  transaction_date: string | null;
  total_amount: number | null;
  currency: string;
  client_reference: string | null;
  iban: string | null;
}

function App() {
  const { user, logout, isAdmin } = useAuth();
  const validTabs = ['receipt', 'review', 'search'] as const;
  type Tab = typeof validTabs[number];
  const hashTab = (): Tab => {
    const h = window.location.hash.replace('#', '') as Tab;
    return validTabs.includes(h) ? h : 'receipt';
  };
  const [activeTab, setActiveTab] = useState<Tab>(hashTab);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [preloadedReceiptId, setPreloadedReceiptId] = useState<number | null>(null);
  const [searchEnabled, setSearchEnabled] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/settings').then(r => setSearchEnabled(r.data.search_enabled === true)).catch(() => {});
    }
  }, [user]);

  const switchTab = (tab: Tab) => {
    if (tab !== activeTab) {
      window.history.pushState(null, '', `#${tab}`);
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    if (!window.location.hash) window.history.replaceState(null, '', `#${activeTab}`);
  }, []);

  useEffect(() => {
    const onPop = () => setActiveTab(hashTab());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (!user) return <LoginPage />;

  const handleViewAnalysis = (id: number) => {
    setPreloadedReceiptId(id);
    switchTab('receipt');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4">
      {/* Toolbar oben rechts */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <span className="text-xs text-gray-500 hidden sm:block">
          {user.email}{isAdmin && <span className="ml-1 text-blue-600 font-medium">(Admin)</span>}
        </span>
        <button
          onClick={() => setIsSettingsModalOpen(true)}
          className="p-2 bg-gray-200 rounded-full shadow-md hover:bg-gray-300"
          title="Einstellungen"
        >
          ⚙️
        </button>
        <button
          onClick={logout}
          className="p-2 bg-gray-200 rounded-full shadow-md hover:bg-gray-300 text-sm"
          title="Abmelden"
        >
          ↩
        </button>
      </div>

      {isSettingsModalOpen && (
        <SettingsModal onClose={() => setIsSettingsModalOpen(false)} />
      )}

      <img src={logo} alt="Ensight Logo" className="h-16 mb-4" />
      <h1 className="text-3xl font-bold text-gray-800 mb-8 tracking-tight">Ensight</h1>

      {/* Tab Navigation */}
      <div className="flex mb-8 w-full max-w-xl">
        <button
          className={`flex-1 py-2 text-lg font-medium ${searchEnabled ? 'rounded-tl-lg' : 'rounded-t-lg'} ${activeTab === 'receipt' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          onClick={() => switchTab('receipt')}
        >
          Rechnungsanalyse
        </button>
        <button
          className={`flex-1 py-2 text-lg font-medium ${searchEnabled ? '' : 'rounded-tr-lg'} ${activeTab === 'review' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          onClick={() => switchTab('review')}
        >
          Prüfung
        </button>
        {searchEnabled && (
          <button
            className={`flex-1 py-2 text-lg font-medium rounded-tr-lg ${activeTab === 'search' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            onClick={() => switchTab('search')}
          >
            Suche
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'receipt' && (
        <ReceiptAnalysisTab
          preloadedReceiptId={preloadedReceiptId}
          onPreloadConsumed={() => setPreloadedReceiptId(null)}
        />
      )}
      {activeTab === 'review' && <ReviewTab />}
      {searchEnabled && activeTab === 'search' && <SearchTab onViewAnalysis={handleViewAnalysis} />}
    </div>
  );
}

export default App;
